import { Component, Input, OnInit } from '@angular/core';
import icBeenhere from '@iconify/icons-ic/twotone-beenhere';
import icStars from '@iconify/icons-ic/twotone-stars';
import icPro from '@iconify/icons-ic/twotone-card-membership';
import icBusinessCenter from '@iconify/icons-ic/twotone-business-center';
import icPhoneInTalk from '@iconify/icons-ic/twotone-phone-in-talk';
import icMail from '@iconify/icons-ic/twotone-mail';
import icTrophy from '@iconify/icons-fa-solid/trophy';
import { stagger60ms } from '../../../../../../@vex/animations/stagger.animation';
import { fadeInUp400ms } from '../../../../../../@vex/animations/fade-in-up.animation';
import { MembershipService } from './membership.service';
import { Icon } from '@visurel/iconify-angular';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from 'src/app/shared/confirm-dialog/confirm-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BaseComponent } from 'src/app/components/base/base.component';
import { TranslateService } from '@ngx-translate/core';
import { SubscriptionProduct } from 'src/app/pages/pages/pricing/interface/subscription-product';
import { PreferenceKeys } from 'src/app/interfaces/preference-keys.enum';
import { Router } from '@angular/router';
import { FormControl } from '@angular/forms';

@Component({
  selector: 'vex-billing',
  templateUrl: './billing.component.html',
  styleUrls: ['./billing.component.scss'],
  animations: [stagger60ms, fadeInUp400ms],
})
export class BillingComponent extends BaseComponent implements OnInit {
  icBeenhere = icBeenhere;
  icStars = icStars;
  icTrophy = icTrophy;
  icBusinessCenter = icBusinessCenter;
  icPhoneInTalk = icPhoneInTalk;
  icMail = icMail;
  icPro = icPro;

  didGetMembership: boolean = false;

  $membershipIcons: Icon[] = [icBeenhere, icStars, icTrophy, icPro, icBusinessCenter];

  // all available tiers
  $allTiers: SubscriptionProduct[] = [];
  // displaying product currently - group by length(monthly or yearly)
  $products: SubscriptionProduct[] = [];

  subscribedIndex$: number = -1;

  isLengthAvailable: boolean = false;
  lengthCtrl = new FormControl('');

  // preferred or selected
  @Input() preferredPackageIndex$: number = -1;

  partner: string = null;

  constructor(
    private membershipService: MembershipService,
    private dialog: MatDialog,
    public snackBar: MatSnackBar,
    public translate: TranslateService,
    private router: Router
  ) {
    super(snackBar, translate);
  }

  ngOnInit() {
    this.getMembership();
  }

  getMembership() {
    const currentUser = JSON.parse(localStorage.getItem(PreferenceKeys.CurrentUser));
    this.membershipService.getProducts(null, currentUser.cid).subscribe(
      response => {
        this.$allTiers = response.subscriptions.map(
          plan => new SubscriptionProduct(plan)
        );

        // grouped by subscription length
        const groupedByLength = this.$allTiers.reduce((accumulator, current) => {
          accumulator[current.length] = accumulator[current.length] || [];
          accumulator[current.length].push(current);
          return accumulator;
        }, Object.create(null));

        this.isLengthAvailable = Object.keys(groupedByLength).length > 1;

        if (this.isLengthAvailable) {
          // this should be replaced based on user's membership
          // this.lengthCtrl.setValue('12');

          // const filtered = this.$allTiers.filter(product => product.length == 12);
          // this.$products = filtered.sort((lhs, rhs) => lhs.price - rhs.price);

          // replacing
          const membership = +localStorage.getItem('membership');
          let subscribedTier = this.$allTiers.find(tier => tier.id == membership);

          if (subscribedTier) {
            const subscribedLength = subscribedTier.length;
            this.lengthCtrl.setValue(`${subscribedLength}`);

            const filtered = this.$allTiers.filter(
              product => product.length == subscribedLength
            );
            this.$products = filtered.sort((lhs, rhs) => lhs.price - rhs.price);
          } else {
            // wasn't able to find it
            // no case to get here but previous logic should be updated as matching to the new one
            this.lengthCtrl.setValue('12');

            const filtered = this.$allTiers.filter(product => product.length == 12);
            this.$products = filtered.sort((lhs, rhs) => lhs.price - rhs.price);
          }
        } else {
          this.$products = this.$allTiers.sort((lhs, rhs) => lhs.price - rhs.price);
        }

        if (this.$products.length > 0) {
          this.partner = this.$products[0].partner;
        }

        const sorted = this.$allTiers.sort((lhs, rhs) => lhs.price - rhs.price);
        localStorage.setItem(PreferenceKeys.MembershipPackages, JSON.stringify(sorted));

        this.membershipService.getMembership().subscribe(
          response => {
            // save it for future use
            const membership$ = response.membership;
            localStorage.setItem('membership', membership$);

            this.didGetMembership = true;

            const subscribedIndex = this.$products.findIndex(
              product => product.id == membership$
            );
            if (subscribedIndex >= 0) {
              this.subscribedIndex$ = subscribedIndex;
            } else {
              // prior to select free
              this.subscribedIndex$ = 0;
            }

            if (this.preferredPackageIndex$ < 0) {
              this.preferredPackageIndex$ = this.subscribedIndex$;
            }
          },
          error => {
            this.didGetMembership = true;
          }
        );
      },
      error => {
        this.didGetMembership = true;
      }
    );
  }

  onPackageSelected(selected) {
    this.preferredPackageIndex$ = selected;
  }

  onSubscribe($index: number) {
    if ($index == 0) {
      const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
        position: { top: '0px' },
        data: {
          title: 'Downgrade Membership?',
          message:
            "You're about to cancel and downgrande your membership to free.<br>Are you sure, you want to downgrade?",
          positiveTitle: 'Downgrade Now',
        },
      });

      confirmDialog.afterClosed().subscribe(result => {
        if (result === true) {
          this.cancelMembership();
        }
      });

      return;
    }

    if (this.subscribedIndex$ === 0) {
      const host = window.location.href.replace(this.router.url, '');
      const request$ = {
        success_url: `${host}/apps/settings/company/membership/success`,
        cancel_url: `${host}/apps/settings/company/membership/failure`,
        price: this.$products[$index].priceID,

        // should be logged as membership in the psMembership table
        sub_id: this.$products[$index].id,
      };

      this.membershipService.subscribe(request$);
    } else {
      const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
        position: { top: '0px' },
        data: {
          title: 'Update Membership?',
          message:
            "You're about to update your membership.<br>Are you sure, you want to update now?",
          positiveTitle: 'Update Now',
        },
      });

      confirmDialog.afterClosed().subscribe(result => {
        if (result === true) {
          this.updateMembership($index);
        }
      });
    }
  }

  contactSales() {
    window.open('https://share.hsforms.com/1lrcAg9WQQgi_pv5A5O6Wiw4d3ql', '_blank');
  }

  cancelMembership() {
    if (this.$products.length <= 0) {
      return;
    }

    const id = this.$products[0].id + '';
    this.membershipService.cancelMembership(id).subscribe(
      response => {
        if (response.STATUS_CODE == '200') {
          this.didCompleteCancelMembership();
        } else {
          this.didFailToCancelMembership();
        }
      },
      error => {
        console.log(error);
        this.didFailToCancelMembership();
      }
    );
  }

  didFailToCancelMembership() {
    this.showError(
      "It's been failed to downgrade your membership, please try again later!"
    );
  }

  didCompleteCancelMembership() {
    this.subscribedIndex$ = 0;

    localStorage.setItem('membership', '0');
    this.membershipService.membership.next(0);

    this.showSuccess('Your company membership has been downgraded successfully!');
  }

  updateMembership(updated: number) {
    const request$ = {
      price: this.$products[updated].priceID,
      sub_id: this.$products[updated].id,
    };

    this.membershipService.updateMembership(request$).subscribe(
      response => {
        if (response.UPDATE.STATUS_CODE == '200') {
          this.didCompleteUpdateMembership(updated);
        } else {
          this.didFailToUpdateMembership();
        }
      },
      error => {
        console.log(error);
        this.didFailToUpdateMembership();
      }
    );
  }

  didFailToUpdateMembership() {
    this.showSuccess(
      "It's been failed to update your membership, please try again later!"
    );
  }

  didCompleteUpdateMembership(updated: number) {
    this.subscribedIndex$ = updated;

    localStorage.setItem('membership', `${updated}`);
    this.membershipService.membership.next(updated);

    this.showSuccess('Your company membership has been updated successfully!');
  }

  getIcon($index) {
    if ($index < this.$membershipIcons.length) {
      return this.$membershipIcons[$index];
    } else {
      return this.$membershipIcons[this.$membershipIcons.length - 1];
    }
  }

  subscriptionLengthChanged(selected: string) {
    const filtered = this.$allTiers.filter(product => product.length == +selected);
    this.$products = filtered.sort((lhs, rhs) => lhs.price - rhs.price);
  }

  getBalanceSaving(product: SubscriptionProduct): any | null {
    const monthlyProducts = this.$allTiers
      .filter(product => product.length == 1)
      .sort((lhs, rhs) => lhs.price - rhs.price);

    const yearlyProducts = this.$allTiers
      .filter(product => product.length == 12)
      .sort((lhs, rhs) => lhs.price - rhs.price);

    const indexOfProduct = yearlyProducts.findIndex(
      existing => existing.id === product.id
    );

    if (indexOfProduct < 0 || indexOfProduct >= monthlyProducts.length) {
      return null;
    }

    const relevantMonthlyProduct = monthlyProducts[indexOfProduct];

    const balanceSaving = relevantMonthlyProduct.price * 12 - product.price;
    const percentageSaving = (balanceSaving / (relevantMonthlyProduct.price * 12)) * 100;

    return {
      balance: balanceSaving,
      percentage: percentageSaving,
    };
  }
}
